import React from 'react'
import { motion } from 'framer-motion'
import { Users, Calendar, MapPin, ShoppingBag, Star, TrendingUp } from 'lucide-react'
import { useBrand } from '../../branding/BrandProvider'

const StatsSection = () => {
  const stats = [
    {
      icon: Users,
      value: '2,500+',
      label: 'Active Musicians',
      description: 'Artists, bands, and music professionals',
      color: 'from-le-accent to-purple-600',
    },
    {
      icon: Calendar,
      value: '150+',
      label: 'Events This Month',
      description: 'Gigs, open mics, and festivals',
      color: 'from-le-accent-2 to-blue-600',
    },
    {
      icon: MapPin,
      value: '75+',
      label: 'Partner Venues',
      description: 'Clubs, bars, and concert halls',
      color: 'from-le-amber to-orange-600',
    },
    {
      icon: ShoppingBag,
      value: '$50K+',
      label: 'Gear Traded',
      description: 'Monthly marketplace volume',
      color: 'from-green-500 to-emerald-600',
    },
    {
      icon: Star,
      value: '4.9/5',
      label: 'Community Rating',
      description: 'Based on 1,200+ reviews',
      color: 'from-yellow-500 to-amber-600',
    },
    {
      icon: TrendingUp,
      value: '25%',
      label: 'Monthly Growth',
      description: 'New members joining',
      color: 'from-pink-500 to-rose-600',
    },
  ]

  const brand = useBrand()
  return (
    <section className="section-padding py-20 bg-gradient-to-b from-transparent to-le-darker/30">
      <div className="container-max">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Powering Wichita's
            <span className="text-gradient block">Music Community</span>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            See how we're bringing together musicians, venues, and music lovers across the city.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon
            return (
              <motion.div
                key={stat.label}
                className="glass-card p-8 text-center hover:bg-white/10 transition-all duration-300 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className={`w-20 h-20 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={36} className="text-white" />
                </div>
                
                <motion.div
                  className="text-4xl lg:text-5xl font-display font-bold text-white mb-2"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.1 + 0.3 }}
                  viewport={{ once: true }}
                >
                  {stat.value}
                </motion.div>
                
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-le-accent transition-colors duration-300">
                  {stat.label}
                </h3>
                
                <p className="text-white/70 leading-relaxed">
                  {stat.description}
                </p>
              </motion.div>
            )
          })}
        </div>

        {/* Additional Info */}
        <motion.div
          className="mt-16 glass-card p-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-display font-semibold text-white mb-4">
            Join the Fastest Growing Music Community in Kansas
          </h3>
          <p className="text-white/70 mb-6 max-w-2xl mx-auto">
            Whether you're a seasoned professional or just starting your musical journey,
            {` ${brand.siteName} `}connects you with the people and opportunities that matter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-primary px-8 py-3">
              Create Your Profile
            </button>
            <button className="btn-secondary px-8 py-3">
              Explore Community
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default StatsSection